import React from 'react';
import ReactDOM from 'react-dom/client';

import { AccessProfilesPage } from '../components/access-profiles-page.jsx';
import { AuditoriaV2Page } from '../components/auditoria-v2.jsx';
import { BillingPage } from '../components/billing-full.jsx';
import { ChangePlanDrawer } from '../components/change-plan-drawer.jsx';
import { TezusContext, TezusProvider } from '../components/context.jsx';
import { Dashboard } from '../components/dashboard.jsx';
import { EquipePermissoesPage } from '../components/equipe-permissoes.jsx';
import { GlobalSearch } from '../components/global-search.jsx';
import { ImpersonationModal, ActiveImpersonationBannerV2 } from '../components/impersonation-modal.jsx';
import { InadimplenciaPage } from '../components/inadimplencia.jsx';
import { IntegrationsPage } from '../components/integrations.jsx';
import { LoginPage } from '../components/login.jsx';
import { LojaDetail } from '../components/loja-detail.jsx';
import { LojasPage, normStatus } from '../components/lojas.jsx';
import {
  FEATURE_IMPERSONATION,
  USE_REAL_BILLING_API,
  saGetShowFuturePages,
  saIsFuturePage,
  saIsMockOnlyPage,
  saIsRealTenantsMode,
  saIsRouteAllowedInMvp,
  saIsPrototypeRoute,
  saResolveLegacyRoute,
  saSetShowFuturePages,
  saShouldUseMockTenants,
} from '../components/mvp-config.jsx';
import { NewStorePage } from '../components/new-store-page.jsx';
import { NotificacoesOperacionaisPage } from '../components/notificacoes-operacionais.jsx';
import { NotificationsPanel } from '../components/notifications.jsx';
import { ObservabilidadePage } from '../components/observabilidade.jsx';
import { ConfigPage, OutOfMvpPage, PlanosLegadoOutOfMvpPage, PlanosPage } from '../components/other-pages.jsx';
import { PlanosPermissoesPage } from '../components/planos-permissoes.jsx';
import { RepairsPage } from '../components/repairs.jsx';
import {
  authService,
  formatBillingStatusError,
  formatOperationalStatusError,
  isRealTenantsMode,
  mapAuthUserToProfileDrawer,
  readAuthState,
  tenantService,
  _notifStore,
} from '../components/services.jsx';
import { Sidebar, Topbar, ImpersonateBanner, ConfirmModal, Toast } from '../components/shell.jsx';
import { SupportKBPage } from '../components/support-kb-page.jsx';
import { SupportReportsPage } from '../components/support-reports-page.jsx';
import { SupportTicketsPage } from '../components/support-tickets-page.jsx';
import { TechSpecPage } from '../components/tech-spec.jsx';
import { TenantCockpit, TenantCockpitMvp } from '../components/tenant-cockpit.jsx';
import { OperationalStatusReasonModal } from '../components/tenant-status.jsx';
import { UserEditorDrawer } from '../components/user-editor-drawer.jsx';
import { STORES } from '../components/data.jsx';


const { useState, useEffect, Component } = React;

    const SA_SAFE_ROUTES = new Set([
      'dashboard', 'lojas', 'loja', 'cockpit', 'nova-loja', 'planos', 'planos-permissoes',
      'inadimplencia', 'billing', 'perfis', 'equipe', 'usuarios', 'config', 'auditoria',
      'integrations', 'observabilidade', 'repairs', 'tickets', 'knowledge-base',
      'support-reports', 'notificacoes-ops', 'tech-spec',
    ]);

    const loadSavedRoute = () => {
      try {
        const saved = JSON.parse(localStorage.getItem('tezus-route') || 'null');
        if (!saved || !saved.page || !SA_SAFE_ROUTES.has(saved.page)) {
          return { page: 'dashboard' };
        }
        if (typeof saIsRouteAllowedInMvp === 'function' && !saIsRouteAllowedInMvp(saved.page)) {
          return { page: 'dashboard', mvpRedirect: true };
        }
        if (typeof saIsMockOnlyPage === 'function' && saIsMockOnlyPage(saved.page)) {
          return { page: 'dashboard', mockOnlyRedirect: true };
        }
        if (
          typeof saIsFuturePage === 'function'
          && saIsFuturePage(saved.page)
          && typeof saGetShowFuturePages === 'function'
          && !saGetShowFuturePages()
        ) {
          return { page: 'dashboard', futureRedirect: true };
        }
        const resolvedPage = typeof saResolveLegacyRoute === 'function'
          ? saResolveLegacyRoute(saved.page)
          : saved.page;
        if (resolvedPage !== saved.page) {
          return { page: resolvedPage, legacyRedirect: true, legacyFrom: saved.page };
        }
        if ((saved.page === 'loja' || saved.page === 'cockpit') && saved.storeId) {
          if (typeof saIsRealTenantsMode === 'function' && saIsRealTenantsMode()) {
            return { page: saved.page, storeId: saved.storeId, pendingStore: true };
          }
          const s = (STORES || []).find(x => x.id === saved.storeId);
          if (s) return { page: saved.page, store: s };
          return { page: 'lojas', storeNotFound: true };
        }
        return { page: saved.page };
      } catch (e) {
        try { localStorage.removeItem('tezus-route'); } catch (_) {}
        return { page: 'dashboard' };
      }
    };

    const resolveTenantId = (storeRef) => {
      if (!storeRef) return null;
      if (storeRef.tenant_id != null) return storeRef.tenant_id;
      const m = String(storeRef.id || '').match(/(\d+)$/);
      return m ? m[1] : storeRef.id;
    };

    const clearSaSession = () => {
      try {
        if (authService?.clearSession) authService.clearSession();
        else localStorage.removeItem('tezus-sa-auth');
      } catch (e) {}
    };

    class SaErrorBoundary extends Component {
      constructor(props) {
        super(props);
        this.state = { error: null };
      }
      static getDerivedStateFromError(error) {
        return { error };
      }
      componentDidCatch(error) {
        console.error('[Super Admin]', error);
      }
      render() {
        if (!this.state.error) return this.props.children;
        return (
          <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-subtle)', padding: 24 }}>
            <div style={{ maxWidth: 420, width: '100%', background: 'var(--bg-elevated)', borderRadius: 12, padding: 28, boxShadow: 'var(--shadow-lg)', textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Não foi possível carregar o Super Admin</div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20, lineHeight: 1.5 }}>
                Ocorreu um erro inesperado na interface. Limpe a sessão e tente novamente.
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button className="btn ghost" type="button" onClick={() => { clearSaSession(); try { localStorage.removeItem('tezus-route'); } catch (e) {} window.location.reload(); }}>Limpar sessão</button>
                <button className="btn primary" type="button" onClick={() => window.location.reload()}>Recarregar</button>
              </div>
            </div>
          </div>
        );
      }
    }

    const TWEAKS = /*EDITMODE-BEGIN*/{
      "tableLayout": "default"
    }/*EDITMODE-END*/;

    
function App() {
      const {
        stores, updateStore, addStore, refetchStores,
        storesLoading, storesError,
        internalUsers, setInternalUsers, auditLog, logAction,
      } = React.useContext(TezusContext);

      const useRealStores = typeof isRealTenantsMode === 'function' && isRealTenantsMode();
      const [statusActionLoading, setStatusActionLoading] = React.useState(false);

      const [theme, setTheme] = useState(() => localStorage.getItem('tezus-theme') || 'light');
      const [route, setRoute] = useState(() => loadSavedRoute());
      const [impersonating,    setImpersonating]    = useState(null);
      const [impersonModal,    setImpersonModal]     = useState(null);
      const [confirmState,     setConfirmState]      = useState(null);
      const [operationalModal, setOperationalModal]  = useState(null);
      const [operationalSaving, setOperationalSaving] = useState(false);
      const [storesListVersion, setStoresListVersion] = useState(0);
      const [planDrawerStore,  setPlanDrawerStore]   = useState(null);
      const [newStoreDrawer,   setNewStoreDrawer]    = useState(false);
      const [notifsOpen,       setNotifsOpen]        = useState(false);
      const [toast,            setToast]             = useState('');
      const [tweaks,           setTweaks]            = useState(TWEAKS);
      const [editMode,         setEditMode]          = useState(false);
      const [authChecking,     setAuthChecking]      = useState(true);
      const [loggedIn,         setLoggedIn]          = useState(false);
      const [authError,        setAuthError]         = useState('');
      const [profileDrawerOpen,setProfileDrawerOpen] = useState(false);
      const [authUser, setAuthUser] = useState(() => (
        typeof readAuthState === 'function' ? readAuthState()?.user : null
      ) || null);
      const [searchOpen,       setSearchOpen]        = useState(false);
      const [showFuturePages,  setShowFuturePages]   = useState(() => (
        typeof saGetShowFuturePages === 'function' ? saGetShowFuturePages() : true
      ));

      const [notifVersion, setNotifVersion] = useState(0);

      const handleShowFuturePagesChange = (next) => {
        const value = typeof saSetShowFuturePages === 'function'
          ? saSetShowFuturePages(next)
          : next === true;
        setShowFuturePages(value);
        if (!value) {
          setNotifsOpen(false);
          if (
            typeof saIsFuturePage === 'function'
            && saIsFuturePage(route.page)
          ) {
            setRoute({ page: 'dashboard' });
          }
        }
      };

      useEffect(() => {
        if (!loggedIn || authChecking) return;
        const mockOnly = typeof saIsMockOnlyPage === 'function' && saIsMockOnlyPage(route.page);
        const futureBlocked = typeof saIsFuturePage === 'function'
          && saIsFuturePage(route.page)
          && !showFuturePages;
        if (mockOnly || futureBlocked) {
          setRoute({ page: 'dashboard' });
        }
      }, [loggedIn, authChecking, route.page, showFuturePages]);

      // Sincroniza contador do sino com o store global de notificações
      useEffect(() => {
        const store = _notifStore;
        if (!store) return;
        const unsub = store.subscribe(() => setNotifVersion(v => v + 1));
        return unsub;
      }, []);

      useEffect(() => {
        let mounted = true;
        const finish = (result) => {
          if (!mounted) return;
          if (result?.authenticated) {
            setLoggedIn(true);
            setAuthError('');
            setAuthUser(result.user || (typeof readAuthState === 'function' ? readAuthState()?.user : null) || null);
          } else if (result?.reason === 'forbidden') {
            setAuthError('Seu usuário não tem permissão para acessar o Super Admin.');
            setLoggedIn(false);
            setAuthUser(null);
          } else if (result?.reason === 'network') {
            setAuthError('Não foi possível conectar ao servidor. Verifique se a API está no ar.');
            setLoggedIn(false);
            setAuthUser(null);
          } else {
            setAuthError('');
            setLoggedIn(false);
            setAuthUser(null);
          }
          setAuthChecking(false);
        };
        if (!authService?.bootstrapSession) {
          finish({ authenticated: false, reason: null });
          return () => { mounted = false; };
        }
        authService.bootstrapSession()
          .then(finish)
          .catch(() => {
            clearSaSession();
            finish({ authenticated: false, reason: 'network' });
          });
        return () => { mounted = false; };
      }, []);

      const handleLogout = async () => {
        await authService.logout();
        setLoggedIn(false);
        setAuthError('');
        setAuthUser(null);
        setProfileDrawerOpen(false);
      };

      const handleLogin  = async ({ email, password }) => {
        if (!authService?.login) {
          const err = new Error('Auth indisponível');
          err.status = 0;
          throw err;
        }
        await authService.login({ email, password });
        setLoggedIn(true);
        setAuthError('');
        setAuthUser(
          (typeof readAuthState === 'function' ? readAuthState()?.user : null) || null
        );
        // Provider may have fetched tenants/notifications before auth; refresh now.
        try { if (refetchStores) await refetchStores(); } catch (e) {}
        try { if (_notifStore?.reload) await _notifStore.reload(); } catch (e) {}
      };

      const handleOpenProfile = async () => {
        let user = authUser || (typeof readAuthState === 'function' ? readAuthState()?.user : null);
        if (!user?.email && authService?.me) {
          try {
            const me = await authService.me();
            user = me?.user || null;
            if (user) setAuthUser(user);
          } catch (e) {
            // keep existing session user if refresh fails
          }
        }
        if (!user?.email) {
          showToast('Dados do perfil não disponíveis.');
          return;
        }
        setProfileDrawerOpen(true);
      };

      useEffect(() => {
        const handler = (e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setSearchOpen(o => !o); }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
      }, []);

      useEffect(() => {
        if (route.page === 'atendimento') setRoute({ page: 'dashboard' });
      }, [route.page]);

      useEffect(() => {
        if (!loggedIn || !route.pendingStore || !route.storeId) return;
        let cancelled = false;
        const run = async () => {
          const sid = route.storeId;
          let store = stores.find(s => s.id === sid || String(s.tenant_id) === String(sid));
          if (!store && tenantService?.getById) {
            try {
              const tid = resolveTenantId({ id: sid, tenant_id: sid });
              const r = await tenantService.getById(tid);
              store = r.data;
            } catch (e) {
              store = null;
            }
          }
          if (cancelled) return;
          if (store) {
            setRoute({ page: route.page, store });
          } else {
            setRoute({ page: 'lojas' });
            showToast('Loja salva não encontrada — redirecionado para Lojas');
          }
        };
        if (!storesLoading) run();
        return () => { cancelled = true; };
      }, [loggedIn, route.pendingStore, route.storeId, route.page, storesLoading, stores.length]);

      useEffect(() => {
        if (route.mvpRedirect) showToast('Módulo fora do MVP — redirecionado ao Dashboard');
        else if (route.legacyRedirect && route.legacyFrom === 'usuarios') showToast('Usuários foi movido para Equipe e Permissões');
        else if (route.storeNotFound) showToast('Loja não encontrada');
      }, []);

      useEffect(() => { document.documentElement.dataset.theme = theme; localStorage.setItem('tezus-theme', theme); }, [theme]);

      useEffect(() => {
        const save = { page: route.page, storeId: route.store?.id };
        localStorage.setItem('tezus-route', JSON.stringify(save));
      }, [route]);

      useEffect(() => {
        const handler = (e) => {
          if (e.data?.type === '__activate_edit_mode') setEditMode(true);
          if (e.data?.type === '__deactivate_edit_mode') setEditMode(false);
        };
        window.addEventListener('message', handler);
        window.parent.postMessage({ type: '__edit_mode_available' }, '*');
        return () => window.removeEventListener('message', handler);
      }, []);

      const setTweak = (k, v) => {
        const next = { ...tweaks, [k]: v };
        setTweaks(next);
        window.parent.postMessage({ type: '__edit_mode_set_keys', edits: { [k]: v } }, '*');
      };

      const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2800); };

      const handleImpersonate = (store) => { setImpersonModal(store); };

      const handleSuspend = (store) => {
        const currentStore = stores.find(s => s.id === store.id) || store;
        const ns = typeof normStatus === 'function' ? normStatus(currentStore.status) : currentStore.status;
        const isSuspend = ns !== 'suspended' && ns !== 'suspenso';
        const useRealBilling = typeof USE_REAL_BILLING_API !== 'undefined' && USE_REAL_BILLING_API;
        const tenantId = resolveTenantId(currentStore);
        setConfirmState({
          title: isSuspend ? `Suspender comercialmente ${currentStore.name}?` : `Reativar comercialmente ${currentStore.name}?`,
          body: isSuspend
            ? <>Altera apenas o <strong>status comercial</strong> (billing). Nesta fase <strong>não bloqueia</strong> automaticamente o acesso da loja.</>
            : <>Restaura o status comercial para ativo. Independente do status operacional.</>,
          confirmLabel: isSuspend ? 'Suspender comercialmente' : 'Reativar comercialmente',
          danger: isSuspend,
          requireReason: useRealBilling,
          minReasonLength: 5,
          onConfirm: async (reason) => {
            if (useRealBilling) {
              setStatusActionLoading(true);
              try {
                await tenantService.updateStatus(tenantId, {
                  status: isSuspend ? 'suspended' : 'active',
                  reason: reason || (isSuspend ? 'Suspensão manual' : 'Reativação manual'),
                  notes: isSuspend ? 'Suspensão manual pelo Super Admin' : 'Reativação manual pelo Super Admin',
                });
                updateStore(currentStore.id, { status: isSuspend ? 'suspended' : 'active' });
                if (refetchStores) await refetchStores();
                showToast(isSuspend ? `${currentStore.name} suspensa` : `${currentStore.name} ativada`);
                setConfirmState(null);
              } catch (e) {
                const msg = typeof formatBillingStatusError === 'function' ? formatBillingStatusError(e) : (e?.message || 'Erro ao alterar status');
                showToast(msg);
              } finally {
                setStatusActionLoading(false);
              }
              return;
            }
            const newStatus = isSuspend ? 'suspended' : 'active';
            updateStore(currentStore.id, { status: newStatus, mrr: newStatus === 'suspended' ? 0 : (currentStore.plan?.price || 0) });
            logAction({ acao: isSuspend ? 'Suspendeu loja' : 'Ativou loja', loja: currentStore.name, lojaId: currentStore.id, kind: isSuspend ? 'danger' : 'info' });
            setConfirmState(null);
            showToast(isSuspend ? `${currentStore.name} suspensa (protótipo local)` : `${currentStore.name} ativada (protótipo local)`);
          },
        });
      };

      const handleOperationalAction = (store, action) => {
        const currentStore = stores.find(s => s.id === store.id) || store;
        setOperationalModal({ store: currentStore, action });
      };

      const handleOperationalConfirm = async ({ reason }) => {
        if (!operationalModal) return;
        const { store, action } = operationalModal;
        const tenantId = resolveTenantId(store);
        setOperationalSaving(true);
        try {
          if (action === 'archive') {
            await tenantService.archive(tenantId, { reason });
          } else {
            await tenantService.reactivate(tenantId, { reason });
          }
          const refreshed = await tenantService.getById(tenantId);
          updateStore(store.id, refreshed.data);
          await handleStoresChanged();
          showToast(action === 'archive' ? 'Loja arquivada. Operação bloqueada; dados e domínio preservados.' : 'Loja reativada. Acesso operacional restaurado.');
          logAction({
            acao: action === 'archive' ? 'Arquivou loja (operacional)' : 'Reativou loja (operacional)',
            loja: store.name,
            lojaId: store.id,
            kind: action === 'archive' ? 'danger' : 'info',
          });
          setOperationalModal(null);
        } catch (e) {
          const msg = typeof formatOperationalStatusError === 'function'
            ? formatOperationalStatusError(e)
            : (e?.message || 'Erro ao alterar status operacional');
          showToast(msg);
        } finally {
          setOperationalSaving(false);
        }
      };

      const handleStoresChanged = async () => {
        if (refetchStores) await refetchStores();
        setStoresListVersion(v => v + 1);
      };

      if (authChecking) {
        return (
          <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-subtle)', color: 'var(--text-secondary)', fontSize: 14 }}>
            Validando sessão...
          </div>
        );
      }

      if (!loggedIn) return <LoginPage onLogin={handleLogin} initialError={authError} />;

      let page;
      const mvpBlocked = typeof saIsRouteAllowedInMvp === 'function' && !saIsRouteAllowedInMvp(route.page);
      const mvpPrototypeOnly = typeof saIsPrototypeRoute === 'function' && saIsPrototypeRoute(route.page);
      const mockOnlyHidden = typeof saIsMockOnlyPage === 'function' && saIsMockOnlyPage(route.page);
      const futureHidden = typeof saIsFuturePage === 'function'
        && saIsFuturePage(route.page)
        && !showFuturePages;

      if (mockOnlyHidden) {
        page = <OutOfMvpPage title="Módulo fora do MVP" onNav={setRoute} />;
      } else if (futureHidden) {
        page = <OutOfMvpPage title="Módulo fora do MVP" onNav={setRoute} />;
      } else if (mvpBlocked) {
        page = <OutOfMvpPage title="Módulo fora do MVP" onNav={setRoute} />;
      } else if (mvpPrototypeOnly && (route.page === 'integrations' || route.page === 'observabilidade')) {
        page = <OutOfMvpPage title={route.page === 'integrations' ? 'Integrations Center' : 'Observabilidade'} onNav={setRoute} />;
      } else if (route.pendingStore) {
        page = (
          <div className="content" style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
            Carregando loja…
          </div>
        );
      } else if (route.page === 'dashboard') {
        page = <Dashboard onNav={setRoute} theme={theme} stores={stores} storesLoading={storesLoading} storesError={storesError} onRetryStores={refetchStores} />;
      }
      else if (route.page === 'lojas')  page = <LojasPage onNav={setRoute} onImpersonate={handleImpersonate} onSuspend={handleSuspend} onOperationalAction={handleOperationalAction} listVersion={storesListVersion} layout={tweaks.tableLayout} onNewStore={() => setNewStoreDrawer(true)} stores={stores} />;
      else if (!mvpBlocked && route.page === 'loja' && route.store) {
        const currentStore = stores.find(s => s.id === route.store.id) || route.store;
        page = <LojaDetail store={currentStore} onNav={setRoute} onImpersonate={handleImpersonate} onSuspend={handleSuspend} onChangePlan={setPlanDrawerStore} onToast={showToast} onUpdate={(updates) => updateStore(updates?.id || currentStore.id, updates)} onStoresChanged={handleStoresChanged} logAction={logAction} />;
      }
      else if (!mvpBlocked && route.page === 'cockpit' && route.store) {
        const currentStore = stores.find(s => s.id === route.store.id) || route.store;
        const useCockpitMock = typeof saShouldUseMockTenants === 'function' && saShouldUseMockTenants();
        page = useCockpitMock
          ? <TenantCockpit store={currentStore} onNav={setRoute} onImpersonate={handleImpersonate} onSuspend={handleSuspend} onChangePlan={setPlanDrawerStore} onToast={showToast} logAction={logAction} />
          : <TenantCockpitMvp store={currentStore} onNav={setRoute} onChangePlan={setPlanDrawerStore} onToast={showToast} />;
      }
      else if (route.page === 'nova-loja') page = <NewStorePage onNav={setRoute} onSave={(store) => {
        addStore(store);
        logAction({ acao: 'Criou tenant', loja: store.name, lojaId: store.id, kind: 'info' });
        showToast(`Tenant "${store.name}" criado com sucesso`);
        setRoute({ page: 'loja', store });
      }} />;
      else if (route.page === 'planos') {
        const usePlanosMock = typeof saShouldUseMockTenants === 'function' && saShouldUseMockTenants();
        page = usePlanosMock
          ? <PlanosPage onToast={showToast} stores={stores} />
          : <PlanosLegadoOutOfMvpPage onNav={setRoute} />;
      }
      else if (route.page === 'planos-permissoes')  page = <PlanosPermissoesPage onToast={showToast} stores={stores} logAction={logAction} tenantContext={route.tenantContext || null} />;
      else if (route.page === 'inadimplencia')      page = <InadimplenciaPage onToast={showToast} stores={stores} />;
      else if (route.page === 'billing')            page = <BillingPage onToast={showToast} onNav={setRoute} stores={stores} tenantContext={route.tenantContext || null} />;
      else if (route.page === 'perfis')             page = <AccessProfilesPage onToast={showToast} logAction={logAction} internalUsers={internalUsers} />;
      else if (route.page === 'equipe')             page = <EquipePermissoesPage onToast={showToast} logAction={logAction} />;
      else if (route.page === 'usuarios')           page = <EquipePermissoesPage onToast={showToast} logAction={logAction} />;
      else if (route.page === 'config')             page = <ConfigPage onToast={showToast} logAction={logAction} />;
      else if (route.page === 'auditoria')          page = <AuditoriaV2Page auditLog={auditLog} tenantContext={route.tenantContext || null} />;
      else if (route.page === 'integrations')       page = <IntegrationsPage onToast={showToast} logAction={logAction} tenantContext={route.tenantContext || null} />;
      else if (route.page === 'observabilidade')    page = <ObservabilidadePage onToast={showToast} logAction={logAction} tenantContext={route.tenantContext || null} />;
      else if (route.page === 'repairs')            page = <RepairsPage onToast={showToast} logAction={logAction} onNav={setRoute} contextTicket={route.contextTicket} tenantContext={route.tenantContext || null} />;
      else if (route.page === 'tickets')         page = <SupportTicketsPage onToast={showToast} onNav={setRoute} stores={stores} initialTicketId={route.openTicketId} />;
      else if (route.page === 'knowledge-base')  page = <SupportKBPage onToast={showToast} onNav={setRoute} articleContext={route.articleContext || null} />;
      else if (route.page === 'support-reports') page = <SupportReportsPage onToast={showToast} />;
      else if (route.page === 'notificacoes-ops')   page = <NotificacoesOperacionaisPage onToast={showToast} onNav={setRoute} />;
      else if (route.page === 'tech-spec')          page = <TechSpecPage onToast={showToast} />;
      else if (!page) page = <OutOfMvpPage onNav={setRoute} />;

      // Notificações (página + sino) ainda são protótipo: só com "páginas futuras".
      const notificationsUiEnabled = showFuturePages === true;

      // Contador do sino — usa o store global (_notifStore) para ser reativo às marcações de leitura
      const unreadCount = (!notificationsUiEnabled || notifsOpen) ? 0 : (
        _notifStore ? _notifStore.getUnreadCount() : 0
      );

      const hasSLAAlert = notificationsUiEnabled && _notifStore
        ? _notifStore.hasSLAAlert()
        : false;

      return (
        <div className="app">
          <Sidebar
            route={route}
            onNav={setRoute}
            onLogout={handleLogout}
            onProfile={handleOpenProfile}
            authUser={authUser}
            showFuturePages={showFuturePages}
            onShowFuturePagesChange={handleShowFuturePagesChange}
          />
          <div style={{ minWidth: 0 }}>
            {impersonating && (
              typeof ActiveImpersonationBannerV2 !== 'undefined'
                ? <ActiveImpersonationBannerV2 store={impersonating} sessionId="imp_active" readOnly={false} onExit={() => { setImpersonating(null); showToast('Modo impersonação encerrado'); logAction({ acao: 'Login como lojista encerrado', loja: impersonating.name, lojaId: impersonating.id, kind: 'info' }); }} />
                : <ImpersonateBanner store={impersonating} onExit={() => { setImpersonating(null); showToast('Modo impersonação encerrado'); }} />
            )}
            <Topbar
              route={route}
              onNav={setRoute}
              theme={theme}
              onToggleTheme={() => setTheme(t => t === 'light' ? 'dark' : 'light')}
              onBell={notificationsUiEnabled ? () => setNotifsOpen(o => !o) : undefined}
              unreadCount={unreadCount}
              hasSLAAlert={hasSLAAlert}
              onSearch={() => setSearchOpen(true)}
            />
            {page}
          </div>

          <ConfirmModal
            open={!!confirmState}
            onClose={() => !statusActionLoading && setConfirmState(null)}
            onConfirm={confirmState?.onConfirm}
            title={confirmState?.title}
            body={confirmState?.body}
            confirmLabel={confirmState?.confirmLabel}
            danger={confirmState?.danger}
            requireReason={confirmState?.requireReason}
            minReasonLength={confirmState?.minReasonLength || 5}
            confirmLoading={statusActionLoading}
          />

          {typeof OperationalStatusReasonModal !== 'undefined' && (
            <OperationalStatusReasonModal
              open={!!operationalModal}
              action={operationalModal?.action}
              tenantName={operationalModal?.store?.name}
              onClose={() => !operationalSaving && setOperationalModal(null)}
              onConfirm={handleOperationalConfirm}
            />
          )}

          <ChangePlanDrawer
            open={!!planDrawerStore}
            store={planDrawerStore}
            onClose={() => setPlanDrawerStore(null)}
            onConfirm={({ plan, apply }) => {
              if (planDrawerStore && plan) {
                updateStore(planDrawerStore.id, { plan, mrr: plan.price || planDrawerStore.mrr });
                logAction({ acao: `Alterou plano para ${plan.name}`, loja: planDrawerStore.name, lojaId: planDrawerStore.id, kind: 'info' });
              }
              setPlanDrawerStore(null);
              if (typeof saIsRealTenantsMode === 'function' && saIsRealTenantsMode()) {
                showToast(`Plano alterado para ${plan?.name || '—'}`);
              } else {
                const when = apply === 'immediately' ? 'imediatamente' : 'no próximo ciclo';
                showToast(`Plano alterado para ${plan?.name} (${when})`);
              }
            }}
          />

          {notificationsUiEnabled && (
            <NotificationsPanel
              open={notifsOpen}
              onClose={() => setNotifsOpen(false)}
              onNav={(r) => { setRoute(r); setNotifsOpen(false); }}
              onToast={showToast}
              stores={stores}
            />
          )}

          {searchOpen && (
            <GlobalSearch
              stores={stores}
              internalUsers={internalUsers}
              storesLoading={storesLoading}
              useRealStores={useRealStores}
              onNav={(r) => { setRoute(r); setSearchOpen(false); }}
              onClose={() => setSearchOpen(false)}
            />
          )}

          <UserEditorDrawer
            open={profileDrawerOpen}
            user={profileDrawerOpen && authUser ? mapAuthUserToProfileDrawer(authUser) : null}
            onClose={() => setProfileDrawerOpen(false)}
            onSave={() => { setProfileDrawerOpen(false); showToast('Perfil atualizado'); }}
          />

          <Toast message={toast} />

          {FEATURE_IMPERSONATION === true && typeof ImpersonationModal !== 'undefined' && (
            <ImpersonationModal
              open={!!impersonModal}
              store={impersonModal}
              onClose={() => setImpersonModal(null)}
              onConfirm={({ store }) => {
                setImpersonating(store);
                setImpersonModal(null);
                showToast(`Sessão iniciada em ${store.name}`);
              }}
              logAction={logAction}
            />
          )}

          {editMode && (
            <div className="tweaks">
              <h4>Tweaks</h4>
              <div className="tweak">
                <label>Layout da tabela de Lojas</label>
                <div className="seg">
                  <button className={tweaks.tableLayout === 'default' ? 'active' : ''} onClick={() => setTweak('tableLayout', 'default')}>Padrão</button>
                  <button className={tweaks.tableLayout === 'compact' ? 'active' : ''} onClick={() => setTweak('tableLayout', 'compact')}>Compacto</button>
                  <button className={tweaks.tableLayout === 'cards'   ? 'active' : ''} onClick={() => setTweak('tableLayout', 'cards')}>Cards</button>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                  {route.page !== 'lojas' && <>Vá até <a onClick={() => setRoute({ page: 'lojas' })} style={{ color: 'var(--primary)', cursor: 'pointer', textDecoration: 'underline' }}>Lojas</a> para ver.</>}
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }


export function mountApp() {
  const rootEl = document.getElementById('root');
  if (!rootEl || !TezusProvider) {
    if (rootEl) {
      rootEl.innerHTML = '<div style="min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px;font-family:Inter,sans-serif;color:#4b5563"><div style="max-width:420px;text-align:center"><p style="font-weight:600;margin-bottom:8px">Não foi possível carregar o Super Admin</p><p style="font-size:14px;margin-bottom:16px">Scripts da aplicação não iniciaram. Recarregue a página ou limpe o cache.</p><button type="button" onclick="localStorage.removeItem(\'tezus-sa-auth\');localStorage.removeItem(\'tezus-route\');location.reload()" style="padding:8px 16px;border-radius:8px;border:1px solid #d1d5db;background:#fff;cursor:pointer">Limpar sessão e recarregar</button></div></div>';
    }
    return;
  }
  try {
    ReactDOM.createRoot(rootEl).render(
      <SaErrorBoundary>
        <TezusProvider><App /></TezusProvider>
      </SaErrorBoundary>
    );
  } catch (e) {
    console.error('[Super Admin] mount failed', e);
    rootEl.innerHTML = '<div style="min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px;font-family:Inter,sans-serif"><div style="max-width:420px;text-align:center"><p style="font-weight:600">Erro ao iniciar o Super Admin</p><p style="font-size:14px;color:#6b7280;margin:12px 0">Limpe a sessão e tente novamente.</p><button type="button" onclick="localStorage.removeItem(\'tezus-sa-auth\');localStorage.removeItem(\'tezus-route\');location.reload()" style="padding:8px 16px;border-radius:8px;border:1px solid #d1d5db;background:#fff;cursor:pointer">Limpar sessão</button></div></div>';
  }
}
